import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasParentProductId, withParentProductId } from "@insite/client-framework/Components/ParentProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { ConfigSectionModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import { AccordionSectionPanelProps } from "@insite/mobius/AccordionSection/AccordionSectionPanel";
import { IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import LazyImage from "@stoneage/MobiusOverrides/LazyImage";
import IconMinus from "@stoneage/XDL/Icons/IconMinus";
import IconPlus from "@stoneage/XDL/Icons/IconPlus";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & HasParentProductId;

const mapStateToProps = (state: ApplicationState, ownProps: HasParentProductId) => ({
    configuration: getProductState(state, ownProps.parentProductId).value?.detail?.configuration,
    productImages: getProductState(state, ownProps.parentProductId).value?.images,
    productConfigImageName: (getSettingsCollection(state) as any)?.["customSettings"]?.[
        "productDetailsPartListImageName"
    ],
});

export interface ProductDetailsKitSectionOptionsStyles {
    wrapper?: InjectableCss;
    configSectionWrapper?: InjectableCss;
    configSectionLabel?: TypographyPresentationProps;
    sectionOptionLinkText?: LinkPresentationProps;
    sectionOptionText?: TypographyPresentationProps;

    accordionSection?: AccordionSectionPresentationProps;
    imageWrapper?: InjectableCss;
    showMoreLessBtn?: LinkPresentationProps;
}

export const productDetailsKitSectionOptionsStyles: ProductDetailsKitSectionOptionsStyles = {
    wrapper: {
        css: css`
            margin-top: 32px;
            background: ${getColor("common.ui4")};
            border: 1px solid ${getColor("common.ui5")};
        `,
    },
    configSectionLabel: {
        weight: "bold",
        size: "16px",
        lineHeight: "22px",
        css: css`
            display: block;
            color: ${getColor("common.black")};
            margin-bottom: 12px;
        `,
    },
    sectionOptionLinkText: {
        typographyProps: {
            size: "14px",
            lineHeight: "22px",
            weight: "normal",
        },
        color: "text",
        css: css`
            display: block;
            padding-left: 12px;
            margin-bottom: 10px;
        `,
    },
    sectionOptionText: {
        size: "14px",
        lineHeight: "22px",
        weight: "normal",
        color: "text",
        css: css`
            display: block;
            padding-left: 12px;
            margin-bottom: 10px;
        `,
    },
    configSectionWrapper: {
        css: css`
            width: 400px;
            margin-bottom: 20px;
            padding-bottom: 20px;
            border-bottom: 1px solid ${getColor("common.ui6")};
            &:last-child,
            &:nth-last-child(2) {
                border-bottom: 0;
            }
            @media (max-width: 1000px) {
                width: calc(50% - 8px);
            }
            @media screen and (max-width: 700px) {
                width: 100%;
            }
        `,
    },
    accordionSection: {
        headerProps: {
            css: css`
                &&&& button {
                    padding: 21px !important;
                    span {
                        font-size: 20px;
                        line-height: 22px;
                        color: ${getColor("common.black")};
                    }
                    ${IconWrapper}, ${IconWrapper} svg {
                        width: 16px;
                        height: 16px;
                    }
                    ${IconWrapper} svg {
                        stroke-width: 4px;
                    }
                }
            `,
        },
        panelProps: {
            css: css`
                background: transparent;
                padding: 0 17px;
                flex-wrap: wrap;
                justify-content: space-between;
                display: ${({ hidden }: AccordionSectionPanelProps) => (hidden ? "none" : "flex")};
            `,
        },
    },
    imageWrapper: {
        css: css`
            background: ${getColor("common.white")};
            border: 1px solid ${getColor("common.ui6")};
            border-radius: 1px;
            margin-bottom: 32px;
            width: 100%;
        `,
    },
    showMoreLessBtn: {
        css: css`
            padding-left: 12px;
            ${IconWrapper} svg path {
                fill: ${getColor("common.black")};
            }
        `,
        typographyProps: {
            size: "14px",
            lineHeight: "18px",
            weight: "bold",
            css: css`
                &&& {
                    color: ${getColor("common.black")};
                }
            `,
        },
    },
};

const styles = productDetailsKitSectionOptionsStyles;

const ProductDetailsKitSectionOptions: React.FC<Props> = ({ configuration, productImages, productConfigImageName }) => {
    if (!configuration || configuration.configSections?.length === 0) {
        return null;
    }

    const configPartsImage = productImages?.filter(
        img => img.name.toLowerCase() === productConfigImageName.toLowerCase(),
    )?.[0];
    const singleOptionConfigs = configuration?.configSections?.filter(
        configSection => configSection.sectionOptions && configSection.sectionOptions?.length === 1,
    );
    const multiOptionConfigs = configuration?.configSections?.filter(
        configSection => configSection.sectionOptions && configSection.sectionOptions?.length > 1,
    );

    if (
        configuration &&
        configuration.configSections?.every(section => !section || section.sectionOptions?.length === 0)
    ) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <Accordion headingLevel={2}>
                <ManagedAccordionSection
                    title={translate("View Complete Part List ({0})", configuration!.configSections!.length.toString())}
                    initialExpanded={false}
                    {...styles.accordionSection}
                >
                    {configPartsImage && (
                        <StyledWrapper {...styles.imageWrapper}>
                            <LazyImage
                                src={configPartsImage.largeImagePath || configPartsImage.mediumImagePath}
                                altText={configPartsImage.imageAltText}
                            />
                        </StyledWrapper>
                    )}
                    {multiOptionConfigs &&
                        multiOptionConfigs?.map(
                            (configSection, index) =>
                                !!configSection.sectionOptions?.length && (
                                    <ProductConfigsPartSection
                                        index={index}
                                        configSection={configSection}
                                        key={configSection.id}
                                    />
                                ),
                        )}
                    {singleOptionConfigs &&
                        singleOptionConfigs?.map(
                            (configSection, index) =>
                                !!configSection.sectionOptions?.length && (
                                    <ProductConfigsPartSection
                                        index={index}
                                        configSection={configSection}
                                        key={configSection.id}
                                    />
                                ),
                        )}
                </ManagedAccordionSection>
            </Accordion>
        </StyledWrapper>
    );
};

type CollapsableConfigSection = {
    index: number;
    configSection: ConfigSectionModel;
};

const ProductConfigsPartSection = ({ index, configSection }: CollapsableConfigSection) => {
    const [showMore, setShowMore] = React.useState(false);
    return (
        <StyledWrapper {...styles.configSectionWrapper}>
            <Typography {...styles.configSectionLabel}>
                {`${configSection.label || configSection.sectionName}`}
            </Typography>
            {configSection.sectionOptions?.slice(0, 2)?.map(sectionOption => (
                <>
                    {sectionOption && sectionOption.name ? (
                        <Link
                            href={`/Product/${sectionOption?.name}`}
                            {...styles.sectionOptionLinkText}
                            key={sectionOption.id}
                        >
                            {sectionOption.description}
                        </Link>
                    ) : (
                        <Typography key={sectionOption.id} {...styles.sectionOptionText}>
                            {sectionOption.description}
                        </Typography>
                    )}
                </>
            ))}
            {!showMore && configSection!.sectionOptions!.length > 2 && (
                <Link
                    {...styles.showMoreLessBtn}
                    onClick={() => setShowMore(true)}
                    icon={{
                        iconProps: {
                            src: IconPlus,
                            size: 8,
                            color: "common.black",
                            css: css`
                                margin-right: 6px;
                            `,
                        },
                    }}
                >
                    {translate("Show More")}
                </Link>
            )}
            {showMore &&
                configSection.sectionOptions?.slice(2, configSection.sectionOptions.length)?.map(sectionOption => (
                    <>
                        {sectionOption && sectionOption.name ? (
                            <Link
                                href={`/Product/${sectionOption?.name}`}
                                {...styles.sectionOptionLinkText}
                                key={sectionOption.id}
                            >
                                {sectionOption.description}
                            </Link>
                        ) : (
                            <Typography key={sectionOption.id} {...styles.sectionOptionText}>
                                {sectionOption.description}
                            </Typography>
                        )}
                    </>
                ))}
            {showMore && configSection!.sectionOptions!.length > 2 && (
                <Link
                    {...styles.showMoreLessBtn}
                    onClick={() => setShowMore(false)}
                    icon={{
                        iconProps: {
                            src: IconMinus,
                            size: 8,
                            color: "common.black",
                            css: css`
                                margin-right: 6px;
                            `,
                        },
                    }}
                >
                    {translate("Show Less")}
                </Link>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withParentProductId(connect(mapStateToProps)(ProductDetailsKitSectionOptions)),
    definition: {
        displayName: "Product Details Configaration Parts",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
